import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplyDiscountDto,
  CancelSubscriptionDto,
  CourtesyUpgradeDto,
  CreateStandaloneSubscriptionDto,
  CreateSubscriptionDto,
  DowngradeSubscriptionDto,
  ExtendTrialDto,
  QuerySubscriptionsDto,
  ReactivateSubscriptionDto,
  SuspendSubscriptionDto,
  TransferSubscriptionDto,
  UpdateSubscriptionDto,
  UpgradeSubscriptionDto,
} from '@control-center/shared';

import {
  applySubscriptionDiscount,
  cancelPendingDowngrade,
  cancelSubscription,
  createStandaloneSubscription,
  createSubscription,
  extendSubscriptionTrial,
  fetchSubscription,
  fetchSubscriptions,
  previewSubscriptionDowngrade,
  previewSubscriptionUpgrade,
  reactivateSubscription,
  removeSubscriptionDiscount,
  revertPendingCancellation,
  scheduleSubscriptionDowngrade,
  suspendSubscription,
  transferSubscription,
  updateSubscription,
  courtesyUpgradeSubscription,
  upgradeSubscription,
  type SubscriptionDetail,
} from '../lib/subscriptions-api';

import { chargesKeys } from './use-charges';
import { clientsKeys } from './use-clients';

export const subscriptionsKeys = {
  all: ['subscriptions'] as const,
  lists: () => [...subscriptionsKeys.all, 'list'] as const,
  list: (filters: QuerySubscriptionsDto) => [...subscriptionsKeys.lists(), filters] as const,
  detail: (id: string) => [...subscriptionsKeys.all, 'detail', id] as const,
};

export function useSubscriptions(filters: QuerySubscriptionsDto = { limit: 50 }) {
  return useQuery({
    queryKey: subscriptionsKeys.list(filters),
    queryFn: () => fetchSubscriptions(filters),
    staleTime: 30_000,
  });
}

export function useSubscription(id: string | undefined) {
  return useQuery<SubscriptionDetail>({
    queryKey: subscriptionsKeys.detail(id ?? ''),
    queryFn: () => fetchSubscription(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSubscriptionDto) => createSubscription(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
    },
  });
}

/**
 * Story 4.13 — cria assinatura standalone (cobrança recorrente sem plano).
 * Invalida subscriptions (nova assinatura) E charges (1ª cobrança inline DP-2).
 */
export function useCreateStandaloneSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStandaloneSubscriptionDto) => createStandaloneSubscription(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

export function useUpdateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSubscriptionDto }) =>
      updateSubscription(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

// =============================================================================
// Cancel / Reactivate — Story 3.6
// =============================================================================

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CancelSubscriptionDto }) =>
      cancelSubscription(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

export function useRevertPendingCancellation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revertPendingCancellation(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(id) });
    },
  });
}

export function useReactivateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: ReactivateSubscriptionDto }) =>
      reactivateSubscription(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

// =============================================================================
// Suspend — Story 3.3
// =============================================================================

export function useSuspendSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SuspendSubscriptionDto }) =>
      suspendSubscription(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

// =============================================================================
// Upgrade — Story 3.4
// =============================================================================

export function usePreviewUpgrade() {
  // Mutation porque é POST. Não cacheamos preview — recalcula sempre.
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpgradeSubscriptionDto }) =>
      previewSubscriptionUpgrade(id, payload),
  });
}

export function useUpgradeSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpgradeSubscriptionDto }) =>
      upgradeSubscription(id, payload),
    onSuccess: (_, vars) => {
      // Invalida subscriptions (lista + detail) + charges (proration nova)
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

/**
 * Cortesia temporária. Invalida charges junto por precaução: se um dia a
 * concessão passar a mexer em cobrança, a tela não fica mostrando dado velho.
 */
export function useCourtesyUpgrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CourtesyUpgradeDto }) =>
      courtesyUpgradeSubscription(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: chargesKeys.all });
    },
  });
}

// =============================================================================
// Downgrade — Story 3.5
// =============================================================================

export function usePreviewDowngrade() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DowngradeSubscriptionDto }) =>
      previewSubscriptionDowngrade(id, payload),
  });
}

export function useScheduleDowngrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: DowngradeSubscriptionDto }) =>
      scheduleSubscriptionDowngrade(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

export function useCancelPendingDowngrade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelPendingDowngrade(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(id) });
    },
  });
}

// =============================================================================
// Transfer — Story 3.7
// =============================================================================

export function useTransferSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TransferSubscriptionDto }) =>
      transferSubscription(id, payload),
    onSuccess: (_, vars) => {
      // Invalida subscriptions (lista + detail) + charges (clientId mudou) + clients
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: chargesKeys.all });
      qc.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}

// =============================================================================
// Discount — Story 3.8
// =============================================================================

export function useApplyDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ApplyDiscountDto }) =>
      applySubscriptionDiscount(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}

export function useRemoveDiscount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeSubscriptionDiscount(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(id) });
    },
  });
}

// =============================================================================
// Trial extend — Story 3.9
// =============================================================================

export function useExtendTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ExtendTrialDto }) =>
      extendSubscriptionTrial(id, payload),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: subscriptionsKeys.all });
      qc.invalidateQueries({ queryKey: subscriptionsKeys.detail(vars.id) });
    },
  });
}
